// The small pieces the Projects list and a Project page share, drawn as the
// Claude Design draws them (screens_projects.jsx: SnapshotTile, StatusDot,
// MiniStages, BudgetBar, the star). The design's colours win over the older
// colour rules (owner, 2026-10-15). Money is ₹ and only reaches these pieces
// when RLS handed the row back.
import type { ReactNode } from 'react';
import { Badge, Mono } from './primitives';
import { Card } from './card';
import { fmtINR } from '../lib/money';
import { fmtHours } from '../lib/issueReports';
import { STAGES, STATE_LABEL, STATE_TONE, countdown, type ProjectState } from '../lib/projects';

export const StateBadge = ({ state }: { state: ProjectState }) => (
  <Badge tone={STATE_TONE[state]} dot>
    {STATE_LABEL[state]}
  </Badge>
);

/** The design's status colours: accent on track, amber at risk, rose blocked, sage shipped. */
// eslint-disable-next-line react-refresh/only-export-components
export const STATE_COLOR: Record<ProjectState, string> = {
  active: 'var(--hf-accent)',
  risk: 'var(--hf-amber)',
  blocked: 'var(--hf-rose)',
  shipped: 'var(--hf-sage)',
};
const STATE_WORD: Record<ProjectState, string> = {
  active: 'on-track',
  risk: 'at-risk',
  blocked: 'blocked',
  shipped: 'shipped',
};

/** A dot and the state in the design's words (on-track, at-risk, blocked, shipped). */
export const StatusDot = ({ state }: { state: ProjectState }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      fontSize: 11,
      color: 'var(--hf-ink-2)',
      whiteSpace: 'nowrap',
    }}
  >
    <span
      aria-hidden="true"
      style={{
        width: 6,
        height: 6,
        borderRadius: 999,
        background: STATE_COLOR[state],
        boxShadow: state === 'active' ? `0 0 6px ${STATE_COLOR[state]}` : undefined,
      }}
    />
    {STATE_WORD[state]}
  </span>
);

/** Each stage's colour (the design's STAGE_COLORS). */
// eslint-disable-next-line react-refresh/only-export-components
export const STAGE_COLOR: Record<string, string> = {
  Concept: 'var(--hf-lilac)',
  Design: 'var(--hf-accent)',
  Production: 'var(--hf-amber)',
  Review: 'var(--hf-sky)',
  Delivery: 'var(--hf-sage)',
};

/** A project's own colour for its stripe: fixed by its id, from the design's palette. */
const STRIPES = [
  'var(--hf-accent)',
  'var(--hf-amber)',
  'var(--hf-sky)',
  'var(--hf-sage)',
  'var(--hf-lilac)',
  'var(--hf-coral)',
  'var(--hf-iris)',
  'var(--hf-cyan)',
];
// eslint-disable-next-line react-refresh/only-export-components
export const projectColor = (id: string): string =>
  STRIPES[([...id].reduce((h, c) => (h * 31 + c.charCodeAt(0)) | 0, 7) >>> 0) % STRIPES.length];

/** Five steps, the ones reached in ink. */
export const MiniStages = ({ idx }: { idx: number }) => (
  <span
    role="img"
    aria-label={`Stage ${Math.min(idx, STAGES.length - 1) + 1} of ${STAGES.length}`}
    style={{ display: 'inline-flex', gap: 2, alignItems: 'center' }}
  >
    {STAGES.map((s, i) => (
      <span
        key={s}
        style={{
          width: 14,
          height: 3,
          borderRadius: 1,
          background: i <= idx ? 'var(--hf-ink)' : 'var(--hf-rule-2)',
        }}
      />
    ))}
  </span>
);

/** "in 12d" / "3d overdue" / "Shipped" under a delivery date; overdue is the one rose case. */
export const Countdown = ({
  deadline,
  today,
  shipped,
}: {
  deadline: string | null;
  today: string;
  shipped: boolean;
}) => {
  const c = countdown(deadline, today, shipped);
  if (!c) return null;
  return (
    <span
      style={{
        fontSize: 'var(--hf-text-2xs)',
        fontFamily: 'var(--hf-font-mono)',
        color: c.late ? 'var(--hf-rose-text)' : 'var(--hf-ink-3)',
      }}
    >
      {c.text}
    </span>
  );
};

/**
 * The design's budget bar: the deal's value, and what the project has cost
 * (money_overview) for those who may see cost. Without it, spent reads "—"
 * and the bar stays empty rather than guessing.
 */
export const BudgetBar = ({ total, used = null }: { total: number; used?: number | null }) => {
  const pct = used == null || total <= 0 ? 0 : Math.min(1, used / total);
  const over = pct > 0.92;
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 90 }}>
      <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Mono style={{ fontSize: 11, color: over ? 'var(--hf-amber-text)' : 'var(--hf-ink-2)' }}>
          {used == null ? '—' : fmtINR(used)}
        </Mono>
        <Mono dim style={{ fontSize: 11 }}>
          /{fmtINR(total)}
        </Mono>
      </span>
      <span
        style={{
          display: 'block',
          height: 3,
          background: 'var(--hf-rule-2)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            display: 'block',
            height: '100%',
            width: `${pct * 100}%`,
            background: over ? 'var(--hf-amber)' : 'var(--hf-accent)',
          }}
        />
      </span>
    </span>
  );
};

/** What a cost figure leaves out, for its note; "" when nothing (finance.ts). */
// eslint-disable-next-line react-refresh/only-export-components
export const costCaveat = (m: { freelancer_hours: number; uncosted_hours: number }): string =>
  [
    m.freelancer_hours > 0 && 'excludes freelancers',
    m.uncosted_hours > 0 && `excludes ${fmtHours(m.uncosted_hours)} with no INR salary on file`,
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * Why a deal-based figure (margin, burn) is "—", called only when there is no
 * INR deal value: no deal, a deal in another currency (never converted), or an
 * INR deal with no amount.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const noDealReason = (currency: string | null): string =>
  !currency
    ? 'no deal linked'
    : currency !== 'INR'
      ? `${currency} deal, not converted`
      : 'deal has no amount';

/** The star (project_stars): a toggle, filled amber when on. */
export const StarButton = ({
  on,
  name,
  onToggle,
  size = 14,
}: {
  on: boolean;
  name: string;
  onToggle: () => void;
  size?: number;
}) => (
  <button
    type="button"
    aria-pressed={on}
    aria-label={on ? `Unstar ${name}` : `Star ${name}`}
    title={on ? 'Starred' : 'Star'}
    onClick={onToggle}
    className="hf-hit"
    style={{
      background: 'transparent',
      border: 0,
      padding: 2,
      cursor: 'pointer',
      display: 'inline-flex',
      color: on ? 'var(--hf-amber-text)' : 'var(--hf-ink-3)',
      opacity: on ? 1 : 0.6,
    }}
  >
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={on ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <polygon points="12 2 15.1 8.3 22 9.3 17 14.1 18.2 21 12 17.8 5.8 21 7 14.1 2 9.3 8.9 8.3" />
    </svg>
  </button>
);

/** The Projects list's snapshot tile (the design's SnapshotTile). */
export const SnapshotTile = ({
  kicker,
  value,
  unit,
  sub,
  color,
  progress,
}: {
  kicker: string;
  /** null: no figure yet, drawn as "—". */
  value: ReactNode;
  unit?: string;
  sub?: ReactNode;
  /** The figure's colour: a hue's -text role (the hue itself in the dark theme). */
  color: string;
  /** 0–1: the thin bar along the foot. */
  progress?: number;
}) => (
  <Card
    style={{
      padding: '14px 16px',
      borderRadius: 12,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <div
      style={{
        fontSize: 'var(--hf-text-2xs)',
        color: 'var(--hf-ink-3)',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
      }}
    >
      {kicker}
    </div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
      <span
        style={{
          fontFamily: 'var(--hf-font-display)',
          fontSize: 28,
          fontWeight: 400,
          letterSpacing: '-0.02em',
          color: value == null ? 'var(--hf-ink-3)' : color,
        }}
      >
        {value ?? '—'}
      </span>
      {unit && value != null && (
        <Mono dim style={{ fontSize: 11 }}>
          {unit}
        </Mono>
      )}
    </div>
    {sub != null && <div style={{ fontSize: 11, color: 'var(--hf-ink-3)' }}>{sub}</div>}
    {progress !== undefined && (
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 2,
          background: 'var(--hf-rule-2)',
        }}
      >
        <div
          style={{ height: '100%', width: `${Math.min(1, progress) * 100}%`, background: color }}
        />
      </div>
    )}
  </Card>
);

// Jira's priority arrows, in the app's hues: high is coral, never the rose that
// means "wrong"; low is sky; medium is ink. The word is beside the icon, or on
// a board card (the Claude Design's) read out and shown on hover instead.
const UP = 'M8 2L3 7h3v7h4V7h3L8 2Z';
const DOWN = 'M8 14L3 9h3V2h4v7h3L8 14Z';
const PRIORITY: Record<string, { color: string; paths: string[] }> = {
  highest: { color: 'var(--hf-coral-text)', paths: [UP, UP] },
  high: { color: 'var(--hf-coral-text)', paths: [UP] },
  medium: { color: 'var(--hf-ink-2)', paths: [] },
  low: { color: 'var(--hf-sky-text)', paths: [DOWN] },
  lowest: { color: 'var(--hf-sky-text)', paths: [DOWN, DOWN] },
};
export const PriorityLabel = ({
  priority,
  iconOnly = false,
}: {
  priority: string;
  iconOnly?: boolean;
}) => {
  const p = PRIORITY[priority.toLowerCase()] ?? PRIORITY.medium;
  return (
    <span
      title={iconOnly ? priority : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 'var(--hf-text-2xs)',
        color: p.color,
      }}
    >
      <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
        {p.paths.length === 0 ? (
          <>
            <rect x="2" y="5" width="12" height="2.5" rx="1.2" fill="currentColor" />
            <rect x="2" y="9" width="12" height="2.5" rx="1.2" fill="currentColor" />
          </>
        ) : (
          p.paths.map((d, i) => (
            <path
              key={i}
              d={d}
              fill="currentColor"
              opacity={i ? 0.45 : 1}
              transform={i ? `translate(0 ${d === UP ? 5 : -5})` : undefined}
            />
          ))
        )}
      </svg>
      {iconOnly ? <span className="hf-sr-only">{priority}</span> : priority}
    </span>
  );
};
